/**
 * Eval judges: default judge models for the eval pipeline, used when an eval
 * matrix does not name a judge itself. Ported from the retired
 * ModelRoutingPanel; the model is now picked from the lane's price list.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { Button } from "../../../ui/button.js";
import { Input } from "../../../ui/input.js";
import { NativeSelect } from "../../../ui/native-select.js";
import { Switch } from "../../../ui/switch.js";
import type { CatalogModel, WorkspaceLite } from "./types.js";
import { EMPTY_CATALOG_LABEL, priceLabel } from "./format.js";
import { JSON_HEADERS, describeError, responseError } from "./http.js";
import {
  ErrorAlert,
  PRINT_CARD_CLASS,
  PRINT_CARD_STYLE,
  TABLE_HEADER_CLASS,
  TABLE_HEADER_STYLE,
} from "./styles.js";

// Eval-judge phases only. Coding sessions follow the global tier models;
// this card exists so eval runs whose
// matrix omits a judge model still have a configured default.
const PHASES = [
  { phase: "eval_absolute_judge", label: "Eval absolute judge" },
  { phase: "eval_pairwise_judge", label: "Eval pairwise judge" },
] as const;

const PROVIDERS = ["fireworks", "baseten"] as const;

type ModelRoutingPhase = (typeof PHASES)[number]["phase"];
type ModelRoutingProvider = (typeof PROVIDERS)[number];

interface ModelRoutingProfile {
  id: string;
  workspaceId: string | null;
  phase: ModelRoutingPhase;
  provider: ModelRoutingProvider;
  model: string;
  temperature: number | null;
  maxTokens: number | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  updatedByUserId: string | null;
}

interface ModelRoutingResponse {
  profiles: ModelRoutingProfile[];
  providerKeys: Record<ModelRoutingProvider, boolean>;
  /** Optional so a stale backend (mid-rolling-deploy) cannot crash the card. */
  catalog?: Partial<Record<ModelRoutingProvider, CatalogModel[]>>;
}

interface ProfileDraft {
  provider: ModelRoutingProvider;
  model: string;
  temperature: string;
  maxTokens: string;
  enabled: boolean;
}

const DEFAULT_PROVIDER_KEYS: Record<ModelRoutingProvider, boolean> = {
  fireworks: false,
  baseten: false,
};

const PROVIDER_LABELS: Record<ModelRoutingProvider, string> = {
  fireworks: "Fireworks",
  baseten: "Baseten",
};

function draftFromProfile(profile: ModelRoutingProfile | undefined): ProfileDraft {
  return {
    provider: profile?.provider ?? "fireworks",
    model: profile?.model ?? "",
    temperature: profile?.temperature == null ? "" : String(profile.temperature),
    maxTokens: profile?.maxTokens == null ? "" : String(profile.maxTokens),
    enabled: profile?.enabled ?? false,
  };
}

function numberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return Number(trimmed);
}

function findProfile(
  profiles: ModelRoutingProfile[],
  phase: ModelRoutingPhase,
  workspaceId: string | null,
): ModelRoutingProfile | undefined {
  return profiles.find(
    (profile) => profile.phase === phase && profile.workspaceId === workspaceId,
  );
}

function sourceProfile(
  profiles: ModelRoutingProfile[],
  phase: ModelRoutingPhase,
  workspaceId: string | null,
): ModelRoutingProfile | undefined {
  return (
    (workspaceId ? findProfile(profiles, phase, workspaceId) : undefined) ??
    findProfile(profiles, phase, null)
  );
}

function draftsFromProfiles(
  profiles: ModelRoutingProfile[],
  workspaceId: string | null,
): Record<ModelRoutingPhase, ProfileDraft> {
  return {
    eval_absolute_judge: draftFromProfile(sourceProfile(profiles, "eval_absolute_judge", workspaceId)),
    eval_pairwise_judge: draftFromProfile(sourceProfile(profiles, "eval_pairwise_judge", workspaceId)),
  };
}

export function EvalJudgesCard({ workspaces }: { workspaces: WorkspaceLite[] }) {
  const [profiles, setProfiles] = useState<ModelRoutingProfile[]>([]);
  const [catalog, setCatalog] = useState<Partial<Record<ModelRoutingProvider, CatalogModel[]>>>({});
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<ModelRoutingPhase, ProfileDraft>>(
    () => draftsFromProfiles([], null),
  );
  const [providerKeys, setProviderKeys] =
    useState<Record<ModelRoutingProvider, boolean>>(DEFAULT_PROVIDER_KEYS);
  const [loading, setLoading] = useState(true);
  const [savingPhase, setSavingPhase] = useState<ModelRoutingPhase | null>(null);
  const [revertingPhase, setRevertingPhase] = useState<ModelRoutingPhase | null>(null);
  const [error, setError] = useState<string | null>(null);

  const profilesByPhase = useMemo(() => {
    const map = new Map<ModelRoutingPhase, ModelRoutingProfile>();
    for (const profile of profiles) {
      if (profile.workspaceId === selectedWorkspaceId) {
        map.set(profile.phase, profile);
      }
    }
    return map;
  }, [profiles, selectedWorkspaceId]);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/model-routing");
      if (!res.ok) {
        setError("Unable to load eval judges.");
        return;
      }
      const body = (await res.json()) as ModelRoutingResponse;
      setProfiles(body.profiles);
      setProviderKeys({ ...DEFAULT_PROVIDER_KEYS, ...body.providerKeys });
      setCatalog(body.catalog ?? {});
      setDrafts(draftsFromProfiles(body.profiles, null));
    } catch (err) {
      setError(describeError(err, "Unable to load eval judges"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProfiles();
  }, [loadProfiles]);

  // Drafts rebuild only when the scope changes (and on load); a save or a
  // revert updates its own phase so the other judge's unsaved edits survive.
  function changeScope(workspaceId: string | null) {
    setSelectedWorkspaceId(workspaceId);
    setDrafts(draftsFromProfiles(profiles, workspaceId));
  }

  function updateDraft(phase: ModelRoutingPhase, update: Partial<ProfileDraft>) {
    setDrafts((prev) => ({
      ...prev,
      [phase]: {
        ...prev[phase],
        ...update,
      },
    }));
  }

  function changeProvider(phase: ModelRoutingPhase, provider: ModelRoutingProvider) {
    const models = catalog[provider] ?? [];
    const current = drafts[phase].model;
    updateDraft(phase, {
      provider,
      model: models.some((m) => m.id === current) ? current : (models[0]?.id ?? current),
    });
  }

  async function savePhase(phase: ModelRoutingPhase) {
    const draft = drafts[phase];
    setSavingPhase(phase);
    setError(null);
    try {
      const url = selectedWorkspaceId
        ? `/api/admin/model-routing/workspaces/${encodeURIComponent(selectedWorkspaceId)}/${encodeURIComponent(phase)}`
        : `/api/admin/model-routing/${encodeURIComponent(phase)}`;
      const res = await fetch(url, {
        method: "PUT",
        headers: JSON_HEADERS,
        body: JSON.stringify({
          provider: draft.provider,
          model: draft.model.trim(),
          temperature: numberOrNull(draft.temperature),
          maxTokens: numberOrNull(draft.maxTokens),
          enabled: draft.enabled,
        }),
      });
      if (!res.ok) {
        setError(await responseError(res));
        return;
      }
      const body = (await res.json()) as { profile: ModelRoutingProfile };
      setProfiles((prev) => [
        ...prev.filter(
          (profile) =>
            !(profile.workspaceId === body.profile.workspaceId && profile.phase === phase),
        ),
        body.profile,
      ]);
      updateDraft(phase, draftFromProfile(body.profile));
    } catch (err) {
      setError(describeError(err, "Unable to save the eval judge"));
    } finally {
      setSavingPhase(null);
    }
  }

  function resetPhase(phase: ModelRoutingPhase) {
    updateDraft(phase, draftFromProfile(sourceProfile(profiles, phase, selectedWorkspaceId)));
  }

  async function revertWorkspacePhase(phase: ModelRoutingPhase) {
    if (!selectedWorkspaceId) return;
    setRevertingPhase(phase);
    setError(null);
    try {
      const res = await fetch(
        `/api/admin/model-routing/workspaces/${encodeURIComponent(selectedWorkspaceId)}/${encodeURIComponent(phase)}`,
        { method: "DELETE" },
      );
      if (!res.ok) {
        setError(await responseError(res));
        return;
      }
      const nextProfiles = profiles.filter(
        (profile) =>
          !(profile.workspaceId === selectedWorkspaceId && profile.phase === phase),
      );
      setProfiles(nextProfiles);
      updateDraft(phase, draftFromProfile(sourceProfile(nextProfiles, phase, selectedWorkspaceId)));
    } catch (err) {
      setError(describeError(err, "Unable to revert the eval judge"));
    } finally {
      setRevertingPhase(null);
    }
  }

  return (
    <section aria-label="Eval judges" className={`${PRINT_CARD_CLASS} flex flex-col gap-3`} style={PRINT_CARD_STYLE}>
      <div>
        <h3 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
          Eval judges
        </h3>
        <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
          Default judge models for the eval pipeline, used when an eval matrix does not name a
          judge model itself. Coding sessions and all other LLM tasks follow Tier models.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 size={20} className="animate-spin" style={{ color: "var(--text-tertiary)" }} />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <label className="flex flex-col gap-1 text-xs" style={{ color: "var(--text-secondary)" }}>
              Eval judge scope
              <NativeSelect
                aria-label="Eval judge scope"
                selectSize="sm"
                value={selectedWorkspaceId ?? ""}
                onChange={(event) => changeScope(event.target.value || null)}
                wrapperClassName="min-w-[220px]"
              >
                <option value="">Global defaults</option>
                {workspaces.map((workspace) => (
                  <option key={workspace.id} value={workspace.id}>
                    {workspace.name} ({workspace.slug})
                  </option>
                ))}
              </NativeSelect>
            </label>
            <div className="flex flex-wrap items-center gap-2">
              {PROVIDERS.map((provider) => (
                <span
                  key={provider}
                  className="rounded-full border px-2.5 py-1 text-xs"
                  style={{
                    borderColor: providerKeys[provider] ? "var(--success)" : "var(--border)",
                    background: providerKeys[provider]
                      ? "var(--success-muted)"
                      : "rgba(var(--glass-rgb), .55)",
                    color: providerKeys[provider] ? "var(--success)" : "var(--text-tertiary)",
                  }}
                >
                  {PROVIDER_LABELS[provider]} key: {providerKeys[provider] ? "configured" : "missing"}
                </span>
              ))}
            </div>
          </div>

          {error && <ErrorAlert message={error} />}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] text-[13px]" style={{ color: "var(--text-primary)" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                  {["Phase", "Provider", "Model", "Temperature", "Max tokens", "Enabled", ""].map((heading) => (
                    <th
                      key={heading || "actions"}
                      className={TABLE_HEADER_CLASS}
                      style={TABLE_HEADER_STYLE}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PHASES.map(({ phase, label }) => {
                  const draft = drafts[phase];
                  const saving = savingPhase === phase;
                  const reverting = revertingPhase === phase;
                  const selectedProfile = profilesByPhase.get(phase);
                  const savedProfile = sourceProfile(profiles, phase, selectedWorkspaceId);
                  const hasWorkspaceOverride = Boolean(selectedWorkspaceId && selectedProfile);
                  const models = catalog[draft.provider] ?? [];
                  const offCatalog = draft.model !== "" && !models.some((m) => m.id === draft.model);
                  return (
                    <tr key={phase}>
                      <td className="border-b px-3 py-2" style={{ borderColor: "var(--border-subtle)" }}>
                        <div className="font-medium">{label}</div>
                        <div className="font-mono text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                          {hasWorkspaceOverride
                            ? "Workspace override"
                            : savedProfile
                              ? "Global override"
                              : "Default fallback"}
                        </div>
                      </td>
                      <td className="border-b px-3 py-2" style={{ borderColor: "var(--border-subtle)" }}>
                        <NativeSelect
                          aria-label={`Provider for ${label}`}
                          selectSize="sm"
                          value={draft.provider}
                          onChange={(event) =>
                            changeProvider(phase, event.target.value as ModelRoutingProvider)
                          }
                        >
                          {PROVIDERS.map((provider) => (
                            <option key={provider} value={provider}>
                              {PROVIDER_LABELS[provider]}
                            </option>
                          ))}
                        </NativeSelect>
                      </td>
                      <td className="border-b px-3 py-2" style={{ borderColor: "var(--border-subtle)" }}>
                        <NativeSelect
                          aria-label={`Model for ${label}`}
                          selectSize="sm"
                          value={draft.model}
                          onChange={(event) => updateDraft(phase, { model: event.target.value })}
                          className="text-xs"
                          wrapperClassName="min-w-[240px]"
                        >
                          {draft.model === "" && (
                            <option value="" disabled>
                              {models.length === 0 ? EMPTY_CATALOG_LABEL : "Pick a model"}
                            </option>
                          )}
                          {offCatalog && (
                            <option value={draft.model} disabled>
                              {`Not on the price list: ${draft.model}`}
                            </option>
                          )}
                          {models.map((m) => (
                            <option key={m.id} value={m.id}>
                              {priceLabel(m)}
                            </option>
                          ))}
                        </NativeSelect>
                      </td>
                      <td className="border-b px-3 py-2" style={{ borderColor: "var(--border-subtle)" }}>
                        <Input
                          aria-label={`Temperature for ${label}`}
                          inputSize="sm"
                          type="number"
                          min="0"
                          max="2"
                          step="0.1"
                          value={draft.temperature}
                          onChange={(event) => updateDraft(phase, { temperature: event.target.value })}
                          placeholder="auto"
                          className="w-24"
                        />
                      </td>
                      <td className="border-b px-3 py-2" style={{ borderColor: "var(--border-subtle)" }}>
                        <Input
                          aria-label={`Max tokens for ${label}`}
                          inputSize="sm"
                          type="number"
                          min="1"
                          step="1"
                          value={draft.maxTokens}
                          onChange={(event) => updateDraft(phase, { maxTokens: event.target.value })}
                          placeholder="auto"
                          className="w-28"
                        />
                      </td>
                      <td className="border-b px-3 py-2" style={{ borderColor: "var(--border-subtle)" }}>
                        <Switch
                          aria-label={`Enabled for ${label}`}
                          checked={draft.enabled}
                          onCheckedChange={(checked) => updateDraft(phase, { enabled: Boolean(checked) })}
                        />
                      </td>
                      <td className="border-b px-3 py-2 text-right" style={{ borderColor: "var(--border-subtle)" }}>
                        <div className="flex justify-end gap-1.5">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() =>
                              hasWorkspaceOverride
                                ? void revertWorkspacePhase(phase)
                                : resetPhase(phase)
                            }
                            loading={reverting}
                            aria-label={`${hasWorkspaceOverride ? "Revert" : "Reset"} ${label}`}
                            className="h-7 px-2 text-xs"
                          >
                            <RotateCcw size={12} />
                            {hasWorkspaceOverride ? "Revert" : "Reset"}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => void savePhase(phase)}
                            loading={saving}
                            disabled={!draft.model.trim()}
                            aria-label={`Save ${label}`}
                            className="h-7 px-2 text-xs"
                          >
                            <Save size={12} />
                            Save
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
